import { useState, type ReactNode, useEffect } from "react";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import BaseTopUpRequest from "./BaseTopUpRequest";
import useApi from "../../services/useApi";
import { useNavigate, useParams } from "react-router";
import endpoints from "../../services/endpoints";
import moment from "moment";

/**
 * Props interface for the AddRaiseETopUpRequest component
 */
type Props = {
  children?: ReactNode;
};

// Define the structure of the transaction data returned from the API
type TransactionDataType = {
  transaction_id: string;
  transaction_date: string;
  requested_user_code: string;
  requested_name: string;
  payable_amount: string;
  advance_payment: string;
};

export default function PayAdvance({ children }: Props) {
  const [transactionData, setTransactionData] = useState<TransactionDataType | null>(null);
  const { getAPI } = useApi();
  const { raiseETopUpRequest } = endpoints;
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const fetchTransactionDetails = async () => {
    setLoading(true);
    try {
      const response = await getAPI(`${raiseETopUpRequest}/${id}`);
      if (!response?.error) {
        setTransactionData(response?.data?.data);
        setTransactionData(response?.data?.data);
      } else {
        navigate("/not-found", {
          state: { route: "/e-top-up-request/e-top-up/raise-e-top-up-request/pay" },
        });
      }
    } catch (error) {
      console.error("Error fetching admin details:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTransactionDetails();
  }, []);

  const transactionFields = [
    "Transaction ID",
    "Transaction Date",
    "Requested User Code",
    "Requested Name",
    "Payable Amount",
    "Advance Payment",
  ];

  const keyMap: Record<string, keyof TransactionDataType> = {
    "Transaction ID": "transaction_id",
    "Transaction Date": "transaction_date",
    "Requested User Code": "requested_user_code",
    "Requested Name": "requested_name",
    "Payable Amount": "payable_amount",
    "Advance Payment": "advance_payment",
  };

  const getKey = (key: string) => {
    switch (key) {
      case 'transaction_date':
        // Safely format the date
        return moment(transactionData?.[key]).format("DD-MM-YYYY, hh:mm A");
      case 'payable_amount':
      case 'advance_payment':
        // Add currency symbol and return the amount
        return `AFA ${transactionData?.[key]}`;
      default:
        // Return the default value if key is not matched
        return transactionData?.[key] || "--";
    }
  };

  const headerContent = (
    <>
      <Breadcrumbs
        navPath={[
          'E Top Up Request', 
          'List of E Top Up Request Raised',
          "Raise E Top Up Request",
          "Pay Advance",
          "Advance Payment E Top Up Request",
        ]}
        heading={"Advance Payment E Top Up Request"}
        noNavigation
        links={['/e-top-up-request/e-top-up', '/e-top-up-request/e-top-up', "/e-top-up-request/e-top-up/raise-e-top-up-request/raise", "/e-top-up-request/e-top-up/raise-e-top-up-request/pay"]}
        hasAddPrivilege={true}
      />
      <div className="bg-white mt-1.5 rounded-lg">
        <div className="px-6 pt-6 pb-2 overflow-x-hidden flex flex-wrap w-full">
          {transactionFields.map((label) => {
            const key = keyMap[label];
            return (
              <div
                className="w-1/3 pb-4"
                key={label}
                data-testid={`field-${key}`}
              >
                <h1 className="text-body2 text-primary_text">{label}</h1>
                {loading ? (
                  <div
                    className="h-5 mt-1 bg-slate-200 animate-pulse rounded-sm w-3/4"
                    data-testid={`skeleton-${key}`}
                  />
                ) : (
                  <p className="text-primary_text text-body1 mt-1" data-testid={key}>
                    {getKey(key)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <BaseTopUpRequest
      showWalletSelection={false}
      showPaymentTypeSelection={false}
      headerContent={headerContent}
      defaultPaymentType="paid"
      transactionData={transactionData}
    >
      {children}
    </BaseTopUpRequest>
  );
}
